import React from "react"
import { graphql } from "gatsby"
import Layout from "../components/layout"
import SEO from "../components/seo"
import PageHero from "../components/pageHero"
import Breadcrumbs from "../components/breadcrumbs"
import TextWithImageBlock from "../components/textWithImageBlock"
import TextBlock from "../components/textBlock"
import Team from "../components/team"
import PageCollection from "../components/pageCollection"
import Embed from "../components/embed"
import Form from "../components/form"
import { getPagePath } from "../utils/pathUtils"
import ProductThumb from "../components/productThumb"
import { Box, Container, Flex, Grid, Heading, Text } from "theme-ui"
import Link from "../components/link"
import NavLink from "../components/navLink"
import SubCategoryThumb from "../components/subCategoryThumb"
import SectionTitle from "../components/sectionTitle"


const List = props => (
  <Box
    {...props}
    as="ul"
    sx={{ listStyle: "none", margin: 0, padding: 0, marginBottom: 3 }}
  />
)

const Item = props => (
  <Box
    {...props}
    as="span"
    sx={{
      borderBottom: "1px solid",
      borderColor: "dark",
      display: "block",
      pb: 2,
      mb: 3,
    }}
  />
)

const Page = ({ data: { page }, location }) => {
  // console.log(page.content);
  const i18nPaths = page._allSlugLocales.map(locale => {
    return {
      locale: locale.locale,
      value: getPagePath(page, locale.locale),
    }
  })

  return (
    <Layout locale={page.locale} location={location} i18nPaths={i18nPaths}>
      <SEO title={page.title} lang={page.locale} />
      <PageHero title={page.title} image={page.heroImage} locale={page.locale}>
        <Breadcrumbs page={page} />
      </PageHero>
      {page.content.map(section => (
        <section key={section.id}>
          {section.model.apiKey === "text_block" && (
            <TextBlock section={section} />
          )}
          {section.model.apiKey === "text_with_image_block" && (
            <TextWithImageBlock section={section} />
          )}
          {section.model.apiKey === "related_category" && (
            <Container mt={5}>
                <div>
                  <SectionTitle title={section.title} />
                  <List>
                      <Grid columns={[1, 2, 2, 3]} gap={[5]}>
                      {section.categories.map(subcategory => (
                        <SubCategoryThumb  subcategory={subcategory}  />
                      ))}
                    </Grid>
                  </List>
                </div>
            </Container>
          )}
          {section.model.apiKey === "team" && (
            <Team title={section.title} people={section.people} />
          )}
          {section.model.apiKey === "page_collection" && (
            <PageCollection title={section.title} pages={section.pages} />
          )}
          {section.model.apiKey === "embed" && (
            <Embed
              title={section.title}
              code={section.code}
              fullWidth={section.fullWidth}
            />
          )}
          {section.model.apiKey === "form" && <Form title={section.title} />}
        </section>
      ))}
    </Layout>
  )
}

export default Page

export const query = graphql`
  query PageQuery($id: String!) {
    page: datoCmsPage(id: { eq: $id }) {
      id
      title
      locale
      root
      ...AllSlugLocales
      heroImage {
        fluid(maxWidth: 1400) {
          ...GatsbyDatoCmsFluid
        }
      }
      content {
        ... on DatoCmsForm {
          id
          title
          model {
            apiKey
          }
        }
        ... on DatoCmsEmbed {
          ...EmbedDetails
        }
        ... on DatoCmsTeam {
          ...teamBlock
        }
        ... on DatoCmsPageCollection {
          ...pageCollectionBlock
        }
        ... on DatoCmsTextBlock {
          id
          title
          body
          model {
            apiKey
          }
        }
        ... on DatoCmsTextWithImageBlock {
          id
          title
          body
          label
          alignement
          dark
          imageGallery {
            fluid(maxWidth: 540) {
              ...GatsbyDatoCmsFluid
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsRelatedCategory {
          id
          title
          categories {
            name
            slug 
            locale
            _allSlugLocales {
              locale
              value
            }
            treeParent {
              slug
              root
              locale
              _allSlugLocales {
                locale
                value
              }
            }
            image {
              fluid(maxWidth: 540) {
                ...GatsbyDatoCmsFluid
              }
            }
          }
          model {
            apiKey
          }
        }
      }
      ...pageParent
    }
  }

  fragment pageParent on DatoCmsPage {
    treeParent {
      slug
      root
      title
      locale
      ...AllSlugLocales
      hide
      treeParent {
        slug
        root
        title
        locale
        ...AllSlugLocales
        hide
      }
    }
  }

  fragment teamBlock on DatoCmsTeam {
    id
    title
    people {
      id
      name
      jobFunction
      email
      image {
        fluid(maxWidth: 700) {
          ...GatsbyDatoCmsFluid
        }
      }
    }
    model {
      apiKey
    }
  }

  fragment EmbedDetails on DatoCmsEmbed {
    title
    code
    fullWidth
    model {
      apiKey
    }
  }

  fragment pageCollectionBlock on DatoCmsPageCollection {
    id
    title
    pages {
      id
      title
      slug
      locale
      ...AllSlugLocales
      ...pageParent
      heroImage {
        fluid(maxWidth: 1920) {
          ...GatsbyDatoCmsFluid
        }
      }
    }
    model {
      apiKey
    }
  }

  fragment AllSlugLocales on DatoCmsPage {
    _allSlugLocales {
      value
      locale
    }
  }
`
